import { useState, useCallback, useEffect, useMemo } from "react"
import {
  FaMapMarkerAlt,
  FaUniversity,
  FaUpload,
  FaClipboardCheck,
  FaUser,
} from "react-icons/fa"
import type { EmployeeOnboardingDetailsDto } from "@/modules/employee/employee/dto/employeeDetails"
import {
  employeeProfileSchema,
  addressSchema,
  bankDetailsSchema,
} from "../../employee/schemas/employeeFormSchemas"
import { ProfileDetailsForm } from "./EmployeePersonalDetailsForm"
import { AddressDetailsForm } from "./AddressDetailsForm"
import { BankDetailsForm } from "./BankDetailsForm"
import { DocumentsUploadForm } from "./DocumentsUploadForm"
import { ReviewSubmitForm } from "./ReviewSubmitForm"
import { EmployeeOnboardingMultiStepFormContainer } from "./EmployeeOnboardingMultiStepFormContainer"
import type { Step } from "./EmployeeOnboardingMultiStepFormHeader"
import { useEmployeeOnboarding } from "../../employee/hooks/employeeHook"
import { Onboardingstatus } from "@/modules/onboarding/onboarding-status"
import { toast } from "sonner"

const steps: Step[] = [
  { id: 0, title: "User Details", icon: FaUser },
  { id: 1, title: "Address", icon: FaMapMarkerAlt },
  { id: 2, title: "Bank Details", icon: FaUniversity },
  { id: 3, title: "Documents", icon: FaUpload },
  { id: 4, title: "Review", icon: FaClipboardCheck },
]

interface EmployeeSelfOnboardingFormProps {
  onboardingData?: EmployeeOnboardingDetailsDto | null
  initialStep?: number
}

export default function EmployeeSelfOnboardingForm({
  onboardingData,
  initialStep = 0,
}: EmployeeSelfOnboardingFormProps) {
  const {
    isSubmitting,
    isSavingData,
    submitOnboarding,
    loadOnboardingData,
    updateOnboardingData,
  } = useEmployeeOnboarding()

  const [currentStep, setCurrentStep] = useState(initialStep)
  const [stepVisited, setStepVisited] = useState<boolean[]>(
    Array(steps.length).fill(false),
  )
  const [formData, setFormData] = useState<EmployeeOnboardingDetailsDto>({
    onboardingId: "",
    onboardingStatus: "",
    seenWelcome: false,
    inviteSent: false,
    notes: [],
    requiredDocuments: [],
    onboardingCreatedAt: "",
    onboardingUpdatedAt: "",
    onboardingStartDate: "",
    latest: false,
    previousOnboardingId: "",
    employeeId: "",
    identityType: "",
    identityNumber: "",
    firstName: "",
    secondName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    employeeStatus: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    bankDetails: {
      bankName: "",
      accountNumber: "",
      branchCode: "",
      accountHolderName: "",
      accountType: "",
    },
    employeeStartDate: "",
    dateOfBirth: "",
    gender: "",
    taxNumber: "",
    raceGroup: "",
    maritalStatus: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    employeeCreatedAt: "",
    employeeUpdatedAt: "",
    organizationId: "",
    organizationName: "",
    fieldFlags: [],
    documentsToBeSigned: [],
  })
  const [response, setResponse] = useState<string | null>(null)
  const [requiredDocFiles, setRequiredDocFiles] = useState<Map<string, File>>(
    new Map(),
  )
  const [signedDocFiles, setSignedDocFiles] = useState<Map<string, File>>(
    new Map(),
  )
  const handleDocumentsChange = useCallback(
    (requiredDocs: Map<string, File>, signedDocs: Map<string, File>) => {
      setRequiredDocFiles(requiredDocs)
      setSignedDocFiles(signedDocs)
    },
    [],
  )

  useEffect(() => {
    if (onboardingData) {
      setFormData((prev) => ({
        ...prev,
        ...onboardingData,
        address: onboardingData.address ?? prev.address,
        bankDetails: onboardingData.bankDetails ?? prev.bankDetails,
      }))
    }
  }, [onboardingData])

  const updateFormData = useCallback(
    (data: Partial<EmployeeOnboardingDetailsDto>) => {
      setFormData((prev) => ({ ...prev, ...data }))
    },
    [],
  )

  // Step validity is derived from the current form data (and the selected
  // document files) using the same schemas the individual step forms use, so
  // it is always accurate regardless of which steps have been mounted/visited.
  // Deriving it here — rather than having each child form report its validity
  // on mount — is what lets the Review step gate Submit correctly even when the
  // user deep-links straight to a later step and never mounts the earlier ones.
  const stepValidity = useMemo<boolean[]>(() => {
    const profileValid = employeeProfileSchema.safeParse({
      firstName: formData.firstName,
      secondName: formData.secondName,
      lastName: formData.lastName,
      idType: formData.identityType,
      idNumber: formData.identityNumber,
      gender: formData.gender,
      dateOfBirth: formData.dateOfBirth,
      phone: formData.phone,
      email: formData.email,
      position: formData.position,
      emergencyContactName: formData.emergencyContactName,
      emergencyContactPhone: formData.emergencyContactPhone,
      taxNumber: formData.taxNumber,
      raceGroup: formData.raceGroup,
      maritalStatus: formData.maritalStatus,
    }).success

    const address =
      typeof formData.address === "string" ? {} : formData.address
    const addressValid = addressSchema.safeParse(address).success

    const bankDetails =
      typeof formData.bankDetails === "string" ? {} : formData.bankDetails
    const bankValid = bankDetailsSchema.safeParse(bankDetails).success

    const requiredDocs = formData.requiredDocuments ?? []
    const requiredDocUploads = formData.requiredDocumentUploads ?? []
    const docsToBeSigned = formData.documentsToBeSigned ?? []
    const hasRequiredDocs =
      requiredDocs.length === 0 ||
      requiredDocs.every(
        (docType) =>
          requiredDocFiles.has(docType) ||
          requiredDocUploads.some(
            (u) => u.documentName === docType && u.submitted,
          ),
      )
    const hasSignedDocs =
      docsToBeSigned.length === 0 ||
      docsToBeSigned
        .filter((d) => !d.hasSigned)
        .every((d) => signedDocFiles.has(d.id))
    const docsValid = hasRequiredDocs && hasSignedDocs

    return [profileValid, addressValid, bankValid, docsValid, false]
  }, [formData, requiredDocFiles, signedDocFiles])

  const isFormValid =
    stepValidity[0] && stepValidity[1] && stepValidity[2] && stepValidity[3]

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setStepVisited((prev) => {
        const newVisited = [...prev]
        newVisited[currentStep] = true
        return newVisited
      })

      if (
        onboardingData?.onboardingStatus !== Onboardingstatus.COMPLETED &&
        onboardingData?.onboardingStatus !== Onboardingstatus.SUBMITTED
      ) {
        // Persist any selected documents alongside the other fields so they
        // survive a logout/login before final submission — the backend
        // update-onboarding endpoint stores these uploads.
        updateOnboardingData(formData, requiredDocFiles, signedDocFiles)
      }
      setCurrentStep(currentStep + 1)
    }
  }, [
    currentStep,
    formData,
    requiredDocFiles,
    signedDocFiles,
    updateOnboardingData,
    onboardingData?.onboardingStatus,
  ])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setStepVisited((prev) => {
        const newVisited = [...prev]
        newVisited[currentStep] = true
        return newVisited
      })
      if (
        onboardingData?.onboardingStatus !== Onboardingstatus.COMPLETED &&
        onboardingData?.onboardingStatus !== Onboardingstatus.SUBMITTED
      ) {
        // Persist any selected documents alongside the other fields so they
        // survive a logout/login before final submission — the backend
        // update-onboarding endpoint stores these uploads.
        updateOnboardingData(formData, requiredDocFiles, signedDocFiles)
      }
      setCurrentStep(currentStep - 1)
    }
  }, [
    currentStep,
    formData,
    requiredDocFiles,
    signedDocFiles,
    updateOnboardingData,
    onboardingData?.onboardingStatus,
  ])

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      if (stepIndex === currentStep) return
      setStepVisited((prev) => {
        const newVisited = [...prev]
        newVisited[currentStep] = true
        return newVisited
      })
      setCurrentStep(Math.min(Math.max(stepIndex, 0), steps.length - 1))
    },
    [currentStep],
  )

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast.error("Please complete all required fields before submitting.")
      return
    }

    try {
      const result = await submitOnboarding(
        formData,
        requiredDocFiles,
        signedDocFiles,
      )
      setResponse(result.message || "Onboarding completed successfully!")
      toast.success(result.message || "Onboarding completed successfully!")
      loadOnboardingData()
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Submission failed. Please try again."
      setResponse(message)
      toast.error(message)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ProfileDetailsForm
            formData={formData}
            onUpdate={updateFormData}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
          />
        )
      case 1:
        return (
          <AddressDetailsForm
            formData={formData.address}
            onUpdate={(address) => updateFormData({ address })}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            fieldFlags={formData.fieldFlags}
          />
        )
      case 2:
        return (
          <BankDetailsForm
            formData={formData.bankDetails}
            onUpdate={(bankDetails) => updateFormData({ bankDetails })}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            fieldFlags={formData.fieldFlags}
          />
        )
      case 3:
        return (
          <DocumentsUploadForm
            requiredDocuments={formData.requiredDocuments}
            requiredDocumentUploads={formData.requiredDocumentUploads}
            documentsToBeSigned={formData.documentsToBeSigned}
            onFileChange={() => {}}
            onDocumentsChange={handleDocumentsChange}
            requiredDocFiles={requiredDocFiles}
            signedDocFiles={signedDocFiles}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            fieldFlags={formData.fieldFlags}
          />
        )
      case 4:
        return (
          <ReviewSubmitForm
            formData={formData}
            response={response}
            onClearResponse={() => setResponse(null)}
            requiredDocFiles={requiredDocFiles}
            signedDocFiles={signedDocFiles}
            onboardingDocs={{
              requiredDocuments: formData.requiredDocuments,
              documentsToBeSigned: formData.documentsToBeSigned,
            }}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
          />
        )
      default:
        return null
    }
  }

  const stepDescriptions: Record<number, string> = {
    0: "Please provide your personal information",
    1: "Enter your residential address details",
    2: "Provide your banking information",
    3: "Upload required documents",
    4: "Review your information before submitting",
  }

  return (
    <EmployeeOnboardingMultiStepFormContainer
      steps={steps}
      currentStep={currentStep}
      stepStatus={stepValidity}
      stepVisited={stepVisited}
      onStepChange={handleStepChange}
      onPrevious={prevStep}
      onNext={nextStep}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isSaving={isSavingData}
      isReadOnly={
        onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
        onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
      }
      stepDescriptions={stepDescriptions}
      submitLabel="Submit"
      canSubmit={isFormValid}
    >
      {renderStepContent()}
    </EmployeeOnboardingMultiStepFormContainer>
  )
}
